/**
 * fr3Doc — the designer's fr3 DOCUMENT backend (design artifact §5.1).
 *
 * Unlike the ZPL document (element model = source of truth, ZPL compiled),
 * an .fr3 file is ALSO read by the real FastReport engine in MySales /
 * MyFashionManager, so the XML itself stays the source of truth and edits are
 * SURGICAL DOM MUTATIONS: only attributes the user actually changed are
 * written; scripts, datasets, print settings and every unknown attribute pass
 * through untouched. Deleting an element removes exactly that node.
 *
 * Serialisation notes:
 *  - the original XML prolog (with its declared encoding) is preserved;
 *  - all non-ASCII characters are emitted as numeric character references,
 *    so the byte stream is plain ASCII and stays valid under ANY declared
 *    8-bit encoding (windows-1252 legacy templates included);
 *  - FastReport writes locale decimals ("3,77953…") — the separator is
 *    detected from the source and reused for every number we write.
 *
 * Editing scope: move / resize / edit text / font size / alignment / frame
 * borders; deletion AND adding new text fields only for flat LABEL templates
 * (the invoice touch-up mode keeps structure).
 *
 * <inherited ParentReport="…"> templates (list reports on the shared
 * tl_portrait.fr3 / tl_landscape.fr3) open MERGED with their parent form(s),
 * supplied by the caller (opts.parents, name -> xml text). Edits still go to
 * the CHILD file only, the way FastReport stores inheritance: an object
 * defined in the child is edited in place; an object that comes from the
 * parent gets (or updates) an <inherited Name="X"> override under the same
 * <inherited> path (page -> band). Parent objects cannot be deleted (FastReport
 * cannot either); new objects go into the child. The parent is never saved.
 */

import { nextId } from './zplModel';

const FR_UNITS_PER_INCH = 96.0;

function unitsToMm(units) {
    return Math.round((units / FR_UNITS_PER_INCH) * 25.4 * 100) / 100;
}

function mmToUnits(mm) {
    return (mm / 25.4) * FR_UNITS_PER_INCH;
}

function mmToUnitsFromDots(dots, dpi) {
    return (dots * FR_UNITS_PER_INCH) / dpi;
}

function attrD(el, name, def) {
    const v = el.getAttribute(name);
    if (v === null || v === '') return def;
    const parsed = parseFloat(String(v).replace(',', '.'));
    return isNaN(parsed) ? def : parsed;
}

function attr(el, name, def) {
    const v = el.getAttribute(name);
    return v === null ? def : v;
}

// EVERY FastReport band class. A band missing from this map is not treated
// as a lane, so its objects inherit the PAGE origin instead of the band's —
// they then pile up on top of the first band's content (this is exactly what
// TfrxReportSummary did on the A4 invoices).
const BAND_TAGS = {
    TfrxReportTitle: 'Report title', TfrxReportSummary: 'Report summary',
    TfrxPageHeader: 'Page header', TfrxPageFooter: 'Page footer',
    TfrxHeader: 'Header', TfrxFooter: 'Footer',
    TfrxMasterData: 'Master data', TfrxDetailData: 'Detail data',
    TfrxSubdetailData: 'Subdetail data', TfrxDataBand4: 'Data band 4',
    TfrxDataBand5: 'Data band 5', TfrxDataBand6: 'Data band 6',
    TfrxChild: 'Child', TfrxSummary: 'Summary',
    TfrxGroupHeader: 'Group header', TfrxGroupFooter: 'Group footer',
    TfrxColumnHeader: 'Column header', TfrxColumnFooter: 'Column footer',
    TfrxOverlay: 'Overlay',
};

const MAX_INHERITANCE_DEPTH = 4;

function parseXmlText(xmlText) {
    let doc;
    try {
        doc = new DOMParser().parseFromString(String(xmlText || '').replace(/^\uFEFF/, ''), 'text/xml');
    } catch (e) {
        return null;
    }
    if (!doc || !doc.documentElement) return null;
    if (doc.getElementsByTagName('parsererror').length > 0) return null;
    return doc;
}

function elementChildren(node) {
    const out = [];
    for (let c = node.firstChild; c; c = c.nextSibling) {
        if (c.nodeType === 1) out.push(c);
    }
    return out;
}

/** Direct children first, then the whole subtree (names are unique per report). */
function findByName(scope, name) {
    if (!name) return null;
    const direct = elementChildren(scope).find((c) => c.getAttribute('Name') === name);
    if (direct) return direct;
    const all = scope.getElementsByTagName('*');
    for (let i = 0; i < all.length; i++) {
        if (all[i].getAttribute('Name') === name) return all[i];
    }
    return null;
}

/** Map every element of an imported subtree to its counterpart in the child file. */
function mapSubtree(merged, child, map) {
    map.set(merged, child);
    const mc = elementChildren(merged);
    const cc = elementChildren(child);
    for (let i = 0; i < mc.length && i < cc.length; i++) mapSubtree(mc[i], cc[i], map);
}

/**
 * FastReport's load-time merge (same rules as the fr3 renderer's merge):
 * attributes override, nested <inherited Name> recurse by name, new
 * components are appended, Datasets/Variables replace the parent's. With a
 * map, every element taken from the overlay is mapped to its node in the
 * overlay document (provenance for the surgical write-back).
 */
function mergeOverlay(target, overlay, map, warnings) {
    const attrs = overlay.attributes;
    for (let i = 0; i < attrs.length; i++) {
        if (attrs[i].name === 'ParentReport') continue;
        target.setAttribute(attrs[i].name, attrs[i].value);
    }
    const owner = target.ownerDocument;
    elementChildren(overlay).forEach((c) => {
        if (c.nodeName === 'inherited') {
            const match = findByName(target, c.getAttribute('Name'));
            if (!match) {
                if (warnings) warnings.push('Inherited object ' + c.getAttribute('Name') + ' not found in the parent form');
                return;
            }
            mergeOverlay(match, c, map, warnings);
            return;
        }
        const imported = owner.importNode(c, true);
        if (c.nodeName === 'Datasets' || c.nodeName === 'Variables') {
            const existing = elementChildren(target).find((t) => t.nodeName === c.nodeName);
            if (existing) target.replaceChild(imported, existing);
            else target.appendChild(imported);
        } else {
            target.appendChild(imported);
        }
        if (map) mapSubtree(imported, c, map);
    });
}

/**
 * The fully merged parent document for an inherited root, or
 * { missing, parentOf } when a parent form is not supplied yet.
 */
function resolveParentDoc(root, ownName, parents, depth, warnings) {
    const parentName = (root.getAttribute('ParentReport') || '').trim();
    if (!parentName) return { error: 'inherited template without ParentReport' };
    if (depth >= MAX_INHERITANCE_DEPTH) return { error: 'inheritance deeper than ' + MAX_INHERITANCE_DEPTH + ' levels' };
    const text = parents ? parents[parentName.toLowerCase()] : null;
    if (!text) return { missing: parentName, parentOf: ownName };
    const parentDoc = parseXmlText(text);
    if (!parentDoc) return { error: 'Parent form ' + parentName + ' is not well-formed XML' };
    const parentRoot = parentDoc.documentElement;
    if (parentRoot.nodeName !== 'inherited') return { doc: parentDoc };
    const grand = resolveParentDoc(parentRoot, parentName, parents, depth + 1, warnings);
    if (!grand.doc) return grand;
    mergeOverlay(grand.doc.documentElement, parentRoot, null, warnings);
    return { doc: grand.doc };
}

/** Names from the report root down to (and including) a merged element. */
function inheritPath(node, root) {
    const path = [];
    for (let n = node; n && n !== root; n = n.parentNode) {
        if (n.nodeType === 1 && n.getAttribute('Name')) path.unshift(n.getAttribute('Name'));
    }
    return path;
}

/**
 * The child-file element that overrides the object at `path`: the existing
 * <inherited Name> chain, created where missing (FastReport's own layout).
 */
function ensureInherited(handle, path) {
    let cur = handle.doc.documentElement;
    path.forEach((name) => {
        let next = elementChildren(cur).find((c) => c.getAttribute('Name') === name);
        if (!next) {
            next = handle.doc.createElement('inherited');
            next.setAttribute('Name', name);
            cur.appendChild(next);
        }
        cur = next;
    });
    return cur;
}

/**
 * Open an .fr3 for editing.
 *
 * @returns { ok, handle, model, lanes, isFlat, warnings } or { ok:false, reason }
 *   handle: opaque state for applyEdits/serialize (doc, node map, separators)
 *   model:  { widthMm, heightMm, dpmm, elements } for the canvas
 *   lanes:  [{ name, topMm, heightMm }] band strips (invoice rendering)
 *   isFlat: no repeating data bands with height -> label-style template
 */
export function openFr3(xmlText, dpmm, opts) {
    const d = dpmm || 8;
    const dpi = Math.round(d * 25.4);
    const options = opts || {};

    let doc;
    try {
        doc = new DOMParser().parseFromString(xmlText, 'text/xml');
    } catch (e) {
        return { ok: false, reason: 'XML parse failed' };
    }
    if (!doc || !doc.documentElement) return { ok: false, reason: 'XML parse failed' };
    if (doc.getElementsByTagName('parsererror').length > 0) {
        return { ok: false, reason: 'Not well-formed XML' };
    }

    const warnings = [];
    // `root` is what the editor walks (the merged report for an inherited
    // file); `doc` stays the CHILD document that gets edited and saved.
    let root = doc.documentElement;
    let mergedDoc = null;
    let provenance = null;
    if (root.nodeName === 'inherited') {
        const parent = resolveParentDoc(root, options.name || '', options.parents, 0, warnings);
        if (parent.missing) {
            return { ok: false, reason: 'inherited', parentName: parent.missing, parentOf: parent.parentOf };
        }
        if (!parent.doc) return { ok: false, reason: parent.error || 'inherited' };
        mergedDoc = parent.doc;
        provenance = new Map();
        mergeOverlay(mergedDoc.documentElement, root, provenance, warnings);
        root = mergedDoc.documentElement;
    }
    if (root.nodeName !== 'TfrxReport') {
        return { ok: false, reason: 'Not a FastReport .fr3 document' };
    }

    // Decimal separator convention of THIS file (Delphi writes locale commas);
    // an inherited child with no decimals of its own follows its parents.
    const sepSource = /="\d+,\d+"/.test(xmlText) || !mergedDoc
        ? xmlText
        : Object.values(options.parents || {}).join(' ');
    const decimalSep = /="\d+,\d+"/.test(sepSource) ? ',' : '.';
    const prologMatch = xmlText.match(/^\s*<\?xml[^>]*\?>/);
    const prolog = prologMatch ? prologMatch[0].trim() : '<?xml version="1.0" encoding="utf-8"?>';

    const model = { widthMm: 50, heightMm: 25, dpmm: d, elements: [] };
    const lanes = [];
    const nodes = {};

    let leftMarginUnits = 0;
    let topMarginUnits = 0;
    // Stacked band positions: the .fr3 stores DESIGNER coordinates, where
    // each band's Top includes the designer's band-caption chrome — printing
    // stacks the bands compactly from the top instead. Displaying raw Tops
    // pushes everything down and clips the bottom, so the editor mirrors the
    // print engine: bands sorted by design Top, stacked by their heights.
    // The same stacked origin is stored per node, so the write-back
    // (absolute mm -> band-relative units) round-trips exactly.
    const bandStackTops = new Map();
    const pages = root.getElementsByTagName('TfrxReportPage');
    if (pages.length > 0) {
        const page = pages[0];
        const pw = attrD(page, 'PaperWidth', 0);
        const ph = attrD(page, 'PaperHeight', 0);
        if (pw > 0) model.widthMm = Math.round(pw * 100) / 100;
        if (ph > 0) model.heightMm = Math.round(ph * 100) / 100;
        leftMarginUnits = mmToUnits(attrD(page, 'LeftMargin', 0));
        topMarginUnits = mmToUnits(attrD(page, 'TopMargin', 0));
        if (pages.length > 1) warnings.push('Multi-page template — only page 1 is editable here');

        const bandNodes = [];
        for (let c = page.firstChild; c; c = c.nextSibling) {
            if (c.nodeType === 1 && BAND_TAGS[c.nodeName]) bandNodes.push(c);
        }
        bandNodes.sort((a, b) => attrD(a, 'Top', 0) - attrD(b, 'Top', 0));
        // Page footers print at the page BOTTOM regardless of design order.
        const paperHeightUnits = mmToUnits(ph > 0 ? ph : 297);
        const bottomMarginUnits = mmToUnits(attrD(page, 'BottomMargin', 0));
        let stack = 0;
        bandNodes.forEach((band) => {
            if (band.nodeName === 'TfrxPageFooter') {
                bandStackTops.set(band,
                    paperHeightUnits - bottomMarginUnits - topMarginUnits - attrD(band, 'Height', 0));
                return;
            }
            bandStackTops.set(band, stack);
            stack += attrD(band, 'Height', 0);
        });
    }

    let dataBands = 0;

    const register = (node, type, bandLeft, bandTop, extra) => {
        const id = nextId();
        const originLeft = leftMarginUnits + bandLeft;
        const originTop = topMarginUnits + bandTop;
        const leftU = attrD(node, 'Left', 0);
        const topU = attrD(node, 'Top', 0);
        const widthU = attrD(node, 'Width', 0);
        const heightU = attrD(node, 'Height', 0);
        const el = Object.assign({
            id, type,
            // The FastReport object Name — what the report script and the
            // event handlers address, so the editor shows it alongside the
            // script.
            name: attr(node, 'Name', ''),
            x: unitsToMm(originLeft + leftU),
            y: unitsToMm(originTop + topU),
            w: unitsToMm(widthU),
            h: unitsToMm(heightU),
        }, extra);
        // Provenance: the child-file node to edit, or - for an object that
        // comes from the parent form - the <inherited> path to override it.
        let target = node;
        let path = null;
        if (provenance) {
            target = provenance.get(node) || null;
            if (!target) {
                path = inheritPath(node, root);
                el.inherited = true;
            }
        }
        nodes[id] = {
            node: target,
            path,
            originLeft, originTop,
            orig: {
                leftU, topU, widthU, heightU,
                text: attr(node, 'Text', null),
                fontU: Math.abs(attrD(node, 'Font.Height', -11)),
                fontSign: attrD(node, 'Font.Height', -11) < 0 ? -1 : 1,
                hAlign: attr(node, 'HAlign', ''),
                frame: Math.round(attrD(node, 'Frame.Typ', 0)),
                fontStyle: Math.round(attrD(node, 'Font.Style', 0)),
                fontName: attr(node, 'Font.Name', 'Arial'),
            },
        };
        model.elements.push(el);
        return el;
    };

    const walk = (node, bandLeft, bandTop) => {
        for (let child = node.firstChild; child; child = child.nextSibling) {
            if (child.nodeType !== 1) continue;
            const tag = child.nodeName;

            if (BAND_TAGS[tag]) {
                // Stacked (print-order) position, not the designer's raw Top.
                const bTop = bandStackTops.has(child) ? bandStackTops.get(child) : attrD(child, 'Top', 0);
                const bHeight = attrD(child, 'Height', 0);
                lanes.push({
                    name: BAND_TAGS[tag] + (child.getAttribute('Name') ? ' · ' + child.getAttribute('Name') : ''),
                    // kind/label/tag drive the designer's band caption bars.
                    kind: BAND_TAGS[tag],
                    label: child.getAttribute('Name') || '',
                    tag,
                    topMm: unitsToMm(topMarginUnits + bTop),
                    heightMm: unitsToMm(bHeight),
                });
                if (tag === 'TfrxMasterData' || tag === 'TfrxDetailData') dataBands += 1;
                walk(child, attrD(child, 'Left', 0), bTop);
                continue;
            }

            if (tag === 'TfrxMemoView') {
                const fontU = Math.abs(attrD(child, 'Font.Height', -11));
                const hAlign = attr(child, 'HAlign', 'haLeft');
                register(child, 'text', bandLeft, bandTop, {
                    text: attr(child, 'Text', ''),
                    font: Math.max(6, Math.round((fontU * dpi) / FR_UNITS_PER_INCH)),
                    fontW: Math.max(6, Math.round((fontU * dpi) / FR_UNITS_PER_INCH)),
                    align: hAlign === 'haCenter' ? 'C' : (hAlign === 'haRight' ? 'R' : (hAlign === 'haBlock' ? 'J' : 'L')),
                    maxLines: 3,
                    rotation: 'N',
                    background: attr(child, 'Color', '') !== '',
                    // Frame.Typ bitmask (Delphi set of ftLeft,ftRight,ftTop,
                    // ftBottom -> 1/2/4/8): most templates draw their rules
                    // as memo frames, not TfrxLineView objects.
                    frame: Math.round(attrD(child, 'Frame.Typ', 0)),
                    frameW: Math.max(1, Math.round((attrD(child, 'Frame.Width', 1) * dpi) / FR_UNITS_PER_INCH)),
                    // Font.Style bitmask (fsBold=1, fsItalic=2, fsUnderline=4).
                    fontStyle: Math.round(attrD(child, 'Font.Style', 0)),
                    fontName: attr(child, 'Font.Name', 'Arial'),
                });
            } else if (tag === 'TfrxBarCodeView') {
                const barType = attr(child, 'BarType', 'bcCode128');
                register(child, 'barcode', bandLeft, bandTop, {
                    data: attr(child, 'Expression', ''),
                    barType: barType.indexOf('EAN13') >= 0 ? 'EAN13'
                        : (barType.indexOf('EAN8') >= 0 ? 'EAN8'
                            : (barType.indexOf('39') >= 0 ? 'CODE39' : 'CODE128')),
                    interpretation: String(attr(child, 'ShowText', 'True')).toLowerCase() !== 'false',
                    module: 2,
                    rotation: 'N',
                });
            } else if (tag === 'TfrxShapeView' || tag === 'TfrxLineView') {
                register(child, 'box', bandLeft, bandTop, {
                    thickness: Math.max(1, Math.round((attrD(child, 'Frame.Width', 1) * dpi) / FR_UNITS_PER_INCH)),
                    fill: 'none',
                });
            } else if (tag === 'TfrxPictureView') {
                register(child, 'logo', bandLeft, bandTop, {});
            } else {
                walk(child, bandLeft, bandTop);
            }
        }
    };

    walk(root, 0, 0);

    if (model.elements.length === 0) {
        return { ok: false, reason: 'No editable objects found (memo/barcode/shape/picture)' };
    }

    const pageNode = root.getElementsByTagName('TfrxReportPage')[0] || null;
    const handle = {
        doc, nodes, decimalSep, prolog, dpi,
        mergedDoc,
        inherited: !!mergedDoc,
        pagePath: mergedDoc && pageNode ? inheritPath(pageNode, root) : null,
    };

    // Reports carry PascalScript and event handlers that address objects BY
    // NAME. Edits pass the script through untouched (byte for byte), but the
    // UI needs to know it is there so it can keep deletions off the table.
    // FastReport stores an empty "begin end." skeleton in every file, so only
    // a script with actual statements (or a wired event handler) counts.
    const scriptBody = (attr(root, 'ScriptText.Text', '') || '')
        .replace(/&#\d+;/g, ' ')
        .replace(/\s+/g, '')
        .toLowerCase();
    const hasScript = (scriptBody !== '' && scriptBody !== 'beginend.' && scriptBody !== 'beginend')
        || /On[A-Za-z]+Print="[^"]+"/.test(xmlText);

    // Objects the script/handlers mention BY NAME: deleting one of these
    // breaks printing, so the editor refuses (or warns about) exactly those
    // instead of blocking every deletion.
    const scriptText = attr(root, 'ScriptText.Text', '') || '';
    const handlerNames = (xmlText.match(/On[A-Za-z]+Print="([^"]+)"/g) || []).join(' ');
    const scriptedNames = {};
    const mentions = (haystack, name) => {
        let from = 0;
        for (;;) {
            const at = haystack.indexOf(name, from);
            if (at < 0) return false;
            const before = at === 0 ? '' : haystack.charAt(at - 1);
            const after = haystack.charAt(at + name.length);
            const wordChar = (c) => c !== '' && /[A-Za-z0-9_]/.test(c);
            if (!wordChar(before) && !wordChar(after)) return true;
            from = at + 1;
        }
    };
    model.elements.forEach((el) => {
        if (!el.name) return;
        if (mentions(scriptText, el.name) || mentions(handlerNames, el.name)) {
            scriptedNames[el.name] = true;
        }
    });

    return {
        ok: true,
        handle,
        model,
        lanes,
        inherited: !!mergedDoc,
        parentReport: mergedDoc ? doc.documentElement.getAttribute('ParentReport') : null,
        isFlat: dataBands === 0,
        hasScript,
        script: scriptText,
        scriptedNames,
        warnings,
    };
}

/** Format a FastReport unit value with the file's own decimal separator. */
function fmtUnits(value, sep) {
    let out = (Math.round(value * 100) / 100).toString();
    if (sep === ',') out = out.replace('.', ',');
    return out;
}

/** A Name attribute no other object in the document (or its parent form) carries. */
function uniqueName(doc, prefix, mergedDoc) {
    const used = {};
    [doc, mergedDoc].forEach((d) => {
        if (!d) return;
        const all = d.getElementsByTagName('*');
        for (let i = 0; i < all.length; i++) {
            const n = all[i].getAttribute ? all[i].getAttribute('Name') : null;
            if (n) used[n] = true;
        }
    });
    let n = 1;
    while (used[prefix + n]) n += 1;
    return prefix + n;
}

/**
 * Apply the editor's element state back onto the DOM — only what changed.
 * Elements missing from `elements` (deleted in the editor) have their nodes
 * removed; elements flagged `isNew` (added in the editor) become fresh
 * TfrxMemoView nodes on the same band as the existing objects. Returns the
 * number of touched nodes.
 */
export function applyFr3Edits(handle, elements) {
    const seen = {};
    let touched = 0;

    elements.forEach((el) => {
        let entry = handle.nodes[el.id];
        if (!entry && el.isNew && el.type === 'text') {
            // Pick the BAND the element was dropped in: the surviving node
            // whose band origin is the closest one at or above it. On a
            // multi-band invoice, anchoring on "any" node would attach a
            // footer field to the page header and print it in the wrong
            // place. Falls back to the topmost band when dropped above all.
            const candidates = Object.keys(handle.nodes)
                .map((key) => handle.nodes[key])
                .filter((candidate) => (candidate.node ? candidate.node.parentNode : candidate.path));
            if (candidates.length === 0) return;
            const dropTopU = mmToUnits(el.y);
            let anchor = null;
            candidates.forEach((candidate) => {
                if (candidate.originTop > dropTopU) return;
                if (!anchor || candidate.originTop > anchor.originTop) anchor = candidate;
            });
            if (!anchor) {
                anchor = candidates.reduce((best, candidate) =>
                    (!best || candidate.originTop < best.originTop ? candidate : best), null);
            }
            const sep = handle.decimalSep;
            const memo = handle.doc.createElement('TfrxMemoView');
            memo.setAttribute('Name', uniqueName(handle.doc, 'WebMemo', handle.mergedDoc));
            // Clamp inside the band: a fresh element dropped above/left of
            // the anchor band's origin would get negative offsets and print
            // off the band.
            memo.setAttribute('Left', fmtUnits(Math.max(0, mmToUnits(el.x) - anchor.originLeft), sep));
            memo.setAttribute('Top', fmtUnits(Math.max(0, mmToUnits(el.y) - anchor.originTop), sep));
            memo.setAttribute('Width', fmtUnits(mmToUnits(el.w || 10), sep));
            memo.setAttribute('Height', fmtUnits(mmToUnits(el.h || 4), sep));
            memo.setAttribute('Font.Charset', '1');
            memo.setAttribute('Font.Color', '0');
            memo.setAttribute('Font.Height',
                String(-Math.max(4, Math.round(mmToUnitsFromDots(el.font || 24, handle.dpi)))));
            memo.setAttribute('Font.Name', el.fontName || 'Arial');
            memo.setAttribute('Font.Style', String(Math.round(el.fontStyle || 0)));
            memo.setAttribute('ParentFont', 'False');
            const newAlign = el.align === 'C' ? 'haCenter'
                : (el.align === 'R' ? 'haRight' : (el.align === 'J' ? 'haBlock' : ''));
            if (newAlign !== '') memo.setAttribute('HAlign', newAlign);
            if ((el.frame || 0) !== 0) memo.setAttribute('Frame.Typ', String(el.frame));
            memo.setAttribute('Text', el.text || '');
            // A band that comes from the parent form gets the new object
            // through its <inherited Name> override in the child file.
            const bandNode = anchor.node
                ? anchor.node.parentNode
                : ensureInherited(handle, anchor.path.slice(0, -1));
            bandNode.appendChild(memo);
            handle.nodes[el.id] = {
                node: memo,
                originLeft: anchor.originLeft, originTop: anchor.originTop,
                orig: {
                    leftU: Math.max(0, mmToUnits(el.x) - anchor.originLeft),
                    topU: Math.max(0, mmToUnits(el.y) - anchor.originTop),
                    widthU: mmToUnits(el.w || 10), heightU: mmToUnits(el.h || 4),
                    text: el.text || '',
                    fontU: Math.abs(mmToUnitsFromDots(el.font || 24, handle.dpi)),
                    fontSign: -1,
                    hAlign: newAlign, frame: el.frame || 0,
                    fontStyle: Math.round(el.fontStyle || 0),
                    fontName: el.fontName || 'Arial',
                },
            };
            seen[el.id] = true;
            touched += 1;
            return;
        }
        if (!entry) return;
        seen[el.id] = true;
        const { originLeft, originTop, orig } = entry;
        const sep = handle.decimalSep;
        // Parent-form objects are written as an <inherited Name> override,
        // created on the first real change only.
        let override = entry.node || null;
        const node = {
            setAttribute: (name, value) => {
                if (!override) override = ensureInherited(handle, entry.path);
                override.setAttribute(name, value);
            },
        };

        const newLeftU = mmToUnits(el.x) - originLeft;
        const newTopU = mmToUnits(el.y) - originTop;
        const newWidthU = mmToUnits(el.w || 0);
        const newHeightU = mmToUnits(el.h || 0);
        let changed = false;

        if (Math.abs(newLeftU - orig.leftU) > 0.05) { node.setAttribute('Left', fmtUnits(newLeftU, sep)); changed = true; }
        if (Math.abs(newTopU - orig.topU) > 0.05) { node.setAttribute('Top', fmtUnits(newTopU, sep)); changed = true; }
        if (Math.abs(newWidthU - orig.widthU) > 0.05) { node.setAttribute('Width', fmtUnits(newWidthU, sep)); changed = true; }
        if (Math.abs(newHeightU - orig.heightU) > 0.05) { node.setAttribute('Height', fmtUnits(newHeightU, sep)); changed = true; }

        if (el.type === 'text') {
            if (orig.text !== null && el.text !== orig.text) {
                node.setAttribute('Text', el.text);
                changed = true;
            }
            const newFontU = mmToUnitsFromDots(el.font || 24, handle.dpi);
            if (Math.abs(newFontU - orig.fontU) > 0.5) {
                node.setAttribute('Font.Height', String(Math.round(newFontU) * orig.fontSign));
                changed = true;
            }
            const alignAttr = el.align === 'C' ? 'haCenter' : (el.align === 'R' ? 'haRight' : (el.align === 'J' ? 'haBlock' : 'haLeft'));
            const origAlign = orig.hAlign === '' ? 'haLeft' : orig.hAlign;
            if (alignAttr !== origAlign) {
                node.setAttribute('HAlign', alignAttr);
                changed = true;
            }
            // Frame borders toggled in the editor (bitmask 1 left / 2 right /
            // 4 top / 8 bottom, like FastReport's set of ftLeft..ftBottom).
            const newFrame = Math.round(el.frame || 0);
            if (newFrame !== orig.frame) {
                node.setAttribute('Frame.Typ', String(newFrame));
                changed = true;
            }
            // Bold/italic/underline (Font.Style set: fsBold=1, fsItalic=2,
            // fsUnderline=4).
            const newStyle = Math.round(el.fontStyle || 0);
            if (newStyle !== orig.fontStyle) {
                node.setAttribute('Font.Style', String(newStyle));
                changed = true;
            }
            // Font family - the till machines are Windows, so any standard
            // Windows font name is safe for FastReport.
            const newFontName = el.fontName || 'Arial';
            if (newFontName !== orig.fontName) {
                node.setAttribute('Font.Name', newFontName);
                changed = true;
            }
        }

        if (changed) touched += 1;
    });

    // Deleted in the editor -> removed from the document. Parent-form
    // objects cannot be removed through inheritance (FastReport cannot
    // either); the editor never offers that, so they are skipped here.
    Object.keys(handle.nodes).forEach((id) => {
        if (!seen[id]) {
            const entry = handle.nodes[id];
            if (entry.node && entry.node.parentNode) {
                entry.node.parentNode.removeChild(entry.node);
                touched += 1;
            }
        }
    });

    return touched;
}

/**
 * Page geometry (TfrxReportPage). Sizes are millimetres, like the attributes
 * themselves. Changing the size makes the paper CUSTOM (PaperSize 256 =
 * DMPAPER_USER); leaving PaperSize on a standard/printer code would let the
 * driver override the millimetres at print time.
 */
export function setFr3Page(handle, patch) {
    let page;
    if (handle.inherited) {
        if (!handle.pagePath) return false;
        page = ensureInherited(handle, handle.pagePath);
    } else {
        const pages = handle.doc.documentElement.getElementsByTagName('TfrxReportPage');
        if (pages.length === 0) return false;
        page = pages[0];
    }
    const sep = handle.decimalSep;
    let sized = false;
    const put = (attribute, value) => {
        if (value === undefined || value === null || isNaN(value)) return;
        page.setAttribute(attribute, fmtUnits(value, sep));
    };
    if (patch.widthMm !== undefined) { put('PaperWidth', patch.widthMm); sized = true; }
    if (patch.heightMm !== undefined) { put('PaperHeight', patch.heightMm); sized = true; }
    put('LeftMargin', patch.leftMm);
    put('RightMargin', patch.rightMm);
    put('TopMargin', patch.topMm);
    put('BottomMargin', patch.bottomMm);
    if (sized) page.setAttribute('PaperSize', '256');
    return true;
}

/** Current page geometry, for the settings form. */
export function getFr3Page(handle) {
    const pages = (handle.mergedDoc || handle.doc).documentElement.getElementsByTagName('TfrxReportPage');
    if (pages.length === 0) return null;
    const page = pages[0];
    return {
        widthMm: attrD(page, 'PaperWidth', 0),
        heightMm: attrD(page, 'PaperHeight', 0),
        leftMm: attrD(page, 'LeftMargin', 0),
        rightMm: attrD(page, 'RightMargin', 0),
        topMm: attrD(page, 'TopMargin', 0),
        bottomMm: attrD(page, 'BottomMargin', 0),
        paperSize: attr(page, 'PaperSize', ''),
    };
}

/**
 * Replace the report script. The DOM stores the DECODED text; serializeFr3
 * re-encodes it in FastReport's own decimal-reference dialect, so newlines
 * and Pascal string quotes survive the round trip.
 */
export function setFr3Script(handle, text) {
    const root = handle.doc.documentElement;
    root.setAttribute('ScriptText.Text', String(text));
    if (!root.getAttribute('ScriptLanguage')) {
        root.setAttribute('ScriptLanguage', 'PascalScript');
    }
}

/**
 * Serialise the document byte-faithful to what FastReport itself writes.
 *
 * FastReport's own XML reader/writer speaks NUMERIC character references
 * (&#34; for quotes, raw utf-8 bytes for the euro sign etc.) - it does not
 * reliably decode the NAMED entities XMLSerializer emits. A `&quot;` inside
 * a memo expression like [DM1.PrintQ."vp"] kills that memo on the Windows
 * side, and an &#8364; for the euro breaks on ANSI FastReport builds. So:
 *  - named quote entities are re-encoded numerically like FastReport does;
 *  - utf-8-declared files (everything FastReport 4 writes) keep raw
 *    non-ASCII characters - encodeBase64 emits proper utf-8 bytes;
 *  - only legacy non-utf8 prologs get the ASCII-only numeric-ref fallback,
 *    so the byte stream stays valid under the declared 8-bit encoding.
 */
export function serializeFr3(handle) {
    let body = new XMLSerializer().serializeToString(handle.doc.documentElement);

    // FastReport's own writer emits DECIMAL numeric references exclusively
    // (verified across real .fr3 files: &#13; &#10; &#34; &#39; &#60; &#62;,
    // zero named entities). Its reader is a hand-rolled Delphi parser, so the
    // hex refs (&#xD;) and named entities (&lt;) XMLSerializer produces are a
    // real corruption risk - above all inside ScriptText.Text, where they sit
    // in the middle of PascalScript. Normalise everything back to decimal.
    body = body
        .replace(/&#x([0-9a-fA-F]+);/g, (m, hex) => '&#' + parseInt(hex, 16) + ';')
        .replace(/&quot;/g, '&#34;')
        .replace(/&apos;/g, '&#39;')
        .replace(/&lt;/g, '&#60;')
        .replace(/&gt;/g, '&#62;')
        .replace(/&amp;/g, '&#38;')
        // XMLSerializer leaves apostrophes raw in "-delimited attributes;
        // FastReport escapes them and they carry PascalScript string
        // literals, so keep its convention.
        .replace(/'/g, '&#39;');
    if (!/encoding="utf-?8"/i.test(handle.prolog)) {
        // eslint-disable-next-line no-control-regex
        body = body.replace(/[\u0080-\uFFFF]/g, (ch) => '&#' + ch.codePointAt(0) + ';');
    }
    return handle.prolog + '\n' + body + '\n';
}

/** utf-8 safe base64 for the ASCII serialisation (it IS ascii, but be safe). */
export function encodeBase64(text) {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}
